import {
  SPARKY_NAV2_ROUTES_EXTPOINT,
  SPARKY_NAV3_ROUTES_EXTPOINT,
  SPARKY_SERVICE_URL_PROVIDER_EXTPOINT,
  SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT,
  SPARKY_TSG_MFE_PROPERTY_FALLBACK_EXTPOINT,
  SPARKY_TSG_ACCESS_AVAIL_EXTPOINT,
  SPARKY_NAV_ITEM_AVAIL_EXTPOINT,
  type IsAllowed,
  type GenerateParams,
  type MfePropertyFallbackConfig,
  type SparkyNavRouteConfig,
  type SetDynamicFallbackProperyFn,
  type SparkyServiceUrlProviderConfig,
  type SparkyFeatureFlagProviderConfig,
  type ProviderMatcher,
  type SparkyNavItemAvailFn,
} from '../../extpoints'
import { FF_SPARKY_USE_SCM_NAV_V3, PROP_TSG_ACCESS_TOKEN_DECODED, PROP_TSG_INSTANCES, SPARKY_FRAMEWORK, TSG_ID_NONE } from '../constants'
import { stateRef } from '../store'
import { createTimeoutPromise, ensureArray, hasOwn, indexArrayReadonly, isFunction, isInSilentLoginIframe, isNonemptyObject, isPromiseLike, isRegExp, isString, noop } from '../utils'
import { getExtensions } from './init'
import { freezeTsgProperties, indexTsgInstances, type Instance, type ReadonlyTsgInstances } from '../tsg'
import { getDatadogRum } from '../externals'
import { overrideTsgInstances } from '../overrideInstances'
import { getFrameworkServiceUrl } from '../states'
import fetch, { type RequestInfo, type RequestInitWithTimeout } from '../../libs/fetch'
import { getMicroAppVars } from '../microApps'
import { _timeouts } from '../../init/performance'
import { __error } from '../../init/debug'
import libs from '../../libs/lazy-ui'
import type { FeatureFlagOptions, IamJwtPayload, ReadonlyTsgProperties, ServiceOptions, TsgProperties, TsgProperty } from '../stateTypes'
import type { ExtensionsTsgCache, ExtensionsTsgCacheKey, NavItem } from '../microAppTypes'

export type FetchFn = (resource: RequestInfo, options?: RequestInitWithTimeout) => Promise<Response>
export const fetchWithEnforcedTimeout: FetchFn = (resource: RequestInfo, options?: RequestInitWithTimeout) => {
  let timeout = options?.timeout || _timeouts.microAppDefaultFetchTimeout || undefined
  if (_timeouts.microAppMaxFetchTimeout &&
    timeout && timeout > _timeouts.microAppMaxFetchTimeout) {
    __error(`Fetch timeout of ${timeout}ms exceeds the maximum of ${_timeouts.microAppMaxFetchTimeout}ms, changed to ${_timeouts.microAppMaxFetchTimeout}ms`)
    timeout = _timeouts.microAppMaxFetchTimeout
  }
  return fetch(resource, { ...options, timeout })
}

const getCache = () => (stateRef.current.extensionManager as { cache?: ExtensionsTsgCache })?.cache

export const resetExtensionsCache = () => {
  getCache()?.clear()
}

export const getCachedValue = <T = any>(key: ExtensionsTsgCacheKey) => getCache()?.get(key) as T | undefined
export const setCachedValue = (key: ExtensionsTsgCacheKey, value: any) => {
  if (value === undefined) {
    getCache()?.delete(key)
  }
  else {
    getCache()?.set(key, value)
  }
}
export const memoPerTsg = <T extends (...args: any[]) => any>(fn: T, key: ExtensionsTsgCacheKey = fn): T => {
  const newFn = (...args: Parameters<T>) => {
    const cache = getCache()
    if (cache) {
      const memoizedValue = cache.get(key)
      if (memoizedValue !== undefined) {
        return memoizedValue
      }
    }
    const result = fn(...args)
    if (cache && result !== undefined) {
      cache.set(key, result)
    }
    return result
  }
  return newFn as T
}

export const ffUseNav3 = () => {
  const { fxVars = {} } = stateRef.current
  if (fxVars.load_nav_extensions && !fxVars.hub && !fxVars.aiops_free && !fxVars.pdf_mode && !isInSilentLoginIframe) {
    const cached = getCachedValue<boolean>(ffUseNav3)
    if (cached !== undefined) {
      return cached
    }
    const flag = getFeatureFlag<boolean>(FF_SPARKY_USE_SCM_NAV_V3)
    if (flag !== undefined) {
      setCachedValue(ffUseNav3, flag)
    }
    return Boolean(flag)
  }
  return false
}

const getRouteExtPoint = () => (
  ffUseNav3() ? SPARKY_NAV3_ROUTES_EXTPOINT : SPARKY_NAV2_ROUTES_EXTPOINT
)

export const runIsAllowed = (isAllowed: IsAllowed | undefined) => {
  if (typeof isAllowed !== 'function') {
    return true
  }
  try {
    return Boolean(isAllowed())
  }
  catch (e) {
    __error('error in isAllowed function:', e)
    return false
  }
}

export type InitedSparkyExtMeta = {
  microAppName?: string
  microAppId?: string
  extensionId?: string
  extensionPointId?: string
}
export type InitedSparkyNavRouteConfig = SparkyNavRouteConfig & { microAppId: string, microAppName: string, extensionId: string }
export const getRouteExtensions = memoPerTsg(() => (
  indexArrayReadonly(getExtensions<InitedSparkyNavRouteConfig>(getRouteExtPoint()), 'extensionId')
))

export const getRouteExtension = (extensionId: string): SparkyNavRouteConfig | undefined => {
  const routeExtensions = stateRef.current.routeExtensions || getRouteExtensions()
  const extension = routeExtensions.get(extensionId)
  return extension
}

export const handleFallbackForTsgProperties = async (props: TsgProperties, noneProps?: TsgProperties, accessTokenDecoded?: IamJwtPayload, tsgInstancesPromise?: Promise<ReadonlyTsgInstances>): Promise<ReadonlyTsgProperties> => {
  const fallbackExtensions = getExtensions(SPARKY_TSG_MFE_PROPERTY_FALLBACK_EXTPOINT)
  const tsgInstancesProp = props[PROP_TSG_INSTANCES]
  if (tsgInstancesProp && Array.isArray(tsgInstancesProp.value)) {
    const insts = overrideTsgInstances(tsgInstancesProp.value as Instance[], props.tsg_id)
    tsgInstancesProp.value = indexTsgInstances(insts, props.tsg_id)
  }
  else if (tsgInstancesPromise) {
    props[PROP_TSG_INSTANCES] = {
      source: 'sparky-fallback',
      revision: 'fallback',
      value: await tsgInstancesPromise,
      updated: Date.now(),
    }
  }
  if (accessTokenDecoded) {
    props[PROP_TSG_ACCESS_TOKEN_DECODED] = {
      source: 'sparky-fallback',
      revision: 'fallback',
      value: accessTokenDecoded,
      updated: Date.now(),
    }
  }
  const properties = freezeTsgProperties(props, noneProps)
  if (fallbackExtensions?.length) {
    const promises = []
    const fallbackProps = {} as TsgProperties
    const fallbackPropsMap = {} as Record<string, MfePropertyFallbackConfig<any> & { microAppId: string }>
    for (const ext of fallbackExtensions) {
      const extension = ext as MfePropertyFallbackConfig<any> & { microAppId: string }
      if (runIsAllowed(extension.isAllowed)) {
        const existing = fallbackPropsMap[extension.key]
        if (existing) {
          const error = new Error(`Duplicate fallback extension found for key: ${ext.key}`)
          console.error(error.message, extension, existing)
          getDatadogRum()?.addError(error, {
            type: 'client_error',
            micro_app_id: extension.microAppId,
          })
        }
        else {
          fallbackPropsMap[ext.key] = extension
        }
      }
    }
    const params = {
      state: stateRef.current,
      stateRef,
      fxVars: stateRef.current.fxVars!,
      sparkyFramework: libs.sparkyFramework,
      fetch: fetchWithEnforcedTimeout,
      tsg_id: props.tsg_id,
      properties,
    } as GenerateParams
    const revision = 'fallback'
    const updated = Date.now()
    const addProp = (key: string, source: string, value: any, async?: boolean) => {
      if (value !== undefined) {
        const prop = { source, value, revision, updated } as TsgProperty
        if (async) {
          prop.async = true
        }
        fallbackProps[key] = prop
      }
    }
    const handleError = (error: Error, microAppId: string, message: string) => {
      if (error.message) {
        error.message = `${message} ${error.message}`
      }
      console.error(message, error)
      getDatadogRum()?.addError(error, {
        type: 'client_error',
        micro_app_id: microAppId,
      })
    }
    for (const { key, source, sync, generate, microAppId } of Object.values(fallbackPropsMap)) {
      if (hasOwn(props, key)) {
        continue
      }
      try {
        const setDynamicFallbackPropery: SetDynamicFallbackProperyFn = ({ key: dynKey, source, value }) => {
          if (!dynKey.startsWith(key) || hasOwn(props, dynKey) || fallbackProps[dynKey]) {
            return false // return false to skip setting the extra fallback property
          }
          addProp(dynKey, source, value)
          return true
        }
        const fallbackResult = generate({ ...params, key, setDynamicFallbackPropery })
        if (fallbackResult === undefined) {
          continue // skip
        }
        if (isPromiseLike(fallbackResult)) {
          if (sync) {
            const [timeoutPromise, clear = noop] = _timeouts.microAppMaxFetchTimeout > 0 ?
              createTimeoutPromise(_timeouts.microAppMaxFetchTimeout + 200) : []
            const promise = Promise.race([
              fallbackResult,
              timeoutPromise,
            ]).then((result) => {
              clear()
              addProp(key, source, result)
            }, (error: Error) => {
              clear()
              handleError(error, microAppId, `Error in fallback extension for key "${key}":`)
            })
            promises.push(promise)
          }
          else {
            addProp(key, source, fallbackResult, true) // directly set promise as value
          }
        }
        else {
          addProp(key, source, fallbackResult)
        }
      }
      catch (err) {
        handleError(err as Error, microAppId, `Error in fallback extension for key "${key}":`)
      }
    }
    if (promises.length) {
      await Promise.all(promises) // no rejections
    }
    const frozen = freezeTsgProperties(Object.assign(fallbackProps, props), noneProps)
    return frozen
  }
  return properties
}

type ServiceUrlProviderConfigWithMatcher = SparkyServiceUrlProviderConfig & {
  matcher: ProviderMatcher
}
type GetServiceUrlFn = (name: string, options?: ServiceOptions) => string | undefined
const initServiceUrlProviders = (): GetServiceUrlFn | undefined => {
  const providers = getCachedValue<GetServiceUrlFn>(SPARKY_SERVICE_URL_PROVIDER_EXTPOINT)
  if (providers) {
    return providers
  }
  const extensions = getExtensions(SPARKY_SERVICE_URL_PROVIDER_EXTPOINT)
  if (!extensions.length) {
    return
  }
  const convertRegExpToFn = (match: SparkyServiceUrlProviderConfig['match']) => {
    return !isRegExp(match) ? match : (name: string) => match.test(name)
  }
  const flattened = extensions.reduce((flattened, ext) => {
    if (runIsAllowed(ext.isAllowed)) {
      if (Array.isArray(ext.match)) {
        for (const match of ext.match) {
          flattened.push({
            ...ext,
            match: convertRegExpToFn(match),
          })
        }
      }
      else {
        flattened.push(ext.match ? {
          ...ext,
          match: convertRegExpToFn(ext.match)
        } : ext)
      }
    }
    return flattened
  }, [] as SparkyServiceUrlProviderConfig[])

  const matchAllExts = [] as SparkyServiceUrlProviderConfig[]
  const nameMapping = {} as Record<string, SparkyServiceUrlProviderConfig>
  const fnMatches = [] as ServiceUrlProviderConfigWithMatcher[]
  for (const ext of flattened) {
    const { match } = ext
    if (!match) {
      matchAllExts.push(ext)
    }
    else if (isString(match)) {
      if (nameMapping[match]) {
        console.error(`ignore duplicated match found for "${match}" in SPARKY_SERVICE_URL_PROVIDER_EXTPOINT extensions.`)
        continue
      }
      else {
        if (match.endsWith('*')) {
          const pattern = match.slice(0, -1)
          const matcher = (name: string) => name.startsWith(pattern)
          fnMatches.push(Object.assign(ext, { matcher }))
        }
        nameMapping[match] = ext
      }
    }
    else if (isFunction(match)) {
      fnMatches.push(Object.assign(ext, { matcher: match }))
    }
    else {
      const error = new Error(`ignore invalid service URL provider extpoint match type: ${typeof match}`)
      console.error(error.message, ext)
      continue
    }
  }
  const getUrl: GetServiceUrlFn = (name: string, options?: ServiceOptions) => {
    const exec = (fn: SparkyServiceUrlProviderConfig['getServiceUrl']) => {
      try {
        return fn(name, options || {}, stateRef.current)
      }
      catch (err) {
        const error = new Error(`error in executing service URL provider for "${name}": ${err}`)
        error.cause = err
        console.error(error.message, err)
        // TODO: datadog
      }
    }
    const isMatch = (fn: ProviderMatcher): boolean => {
      try {
        return Boolean(fn(name))
      }
      catch (err) {
        const error = new Error(`error in executing service URL provider for "${name}": ${err}`)
        error.cause = err
        console.error(error.message, err)
        // TODO: datadog
        return false
      }
    }
    const exactMatched = nameMapping[name]
    if (exactMatched) {
      const result = exec(exactMatched.getServiceUrl)
      if (result || !exactMatched.allowFallback) {
        return result
      }
    }
    for (const ext of fnMatches) {
      if (isMatch(ext.matcher)) {
        const result = exec(ext.getServiceUrl)
        if (result || !ext.allowFallback) {
          return result
        }
      }
    }
    for (const ext of matchAllExts) {
      const result = exec(ext.getServiceUrl)
      if (result || !ext.allowFallback) {
        return result
      }
    }
  }
  setCachedValue(SPARKY_SERVICE_URL_PROVIDER_EXTPOINT, getUrl)
  return getUrl
}
export const getServiceUrl = (name: string, options?: ServiceOptions): string | undefined => {
  const cacheKey = isNonemptyObject(options) ? undefined : // only use cache if no options are provided
    `${SPARKY_SERVICE_URL_PROVIDER_EXTPOINT.id}:${name}`
  const cached = cacheKey ? getCachedValue<string>(cacheKey) : undefined
  if (cached) {
    return cached
  }
  const getUrl = initServiceUrlProviders()
  const result = getUrl?.(name, options) ?? getFrameworkServiceUrl(name, options)
  if (result && cacheKey && !stateRef.current.auth?.initializing) {
    setCachedValue(cacheKey, result)
  }
  return result
}

export type TsgAccessAvailError = Error & {
  message: string
  code?: string
}
export type TsgAccessAvailResult = {
  isAvail: boolean
  error?: TsgAccessAvailError
}
export const tsgAccessExtAvailResult = (): TsgAccessAvailResult => {
  const cached = getCachedValue<TsgAccessAvailResult>(SPARKY_TSG_ACCESS_AVAIL_EXTPOINT)
  if (cached?.isAvail !== undefined) {
    return cached
  }
  const extensions = getExtensions(SPARKY_TSG_ACCESS_AVAIL_EXTPOINT)
  for (const ext of extensions) {
    if (runIsAllowed(ext.isAllowed) && isFunction(ext.tsgAvail)) {
      try {
        const result = ext.tsgAvail()
        if (!result) {
          const res = { isAvail: false }
          setCachedValue(SPARKY_TSG_ACCESS_AVAIL_EXTPOINT, res)
          return res
        }
      }
      catch (e) {
        const error: TsgAccessAvailError = (e as TsgAccessAvailError)?.message ? e as TsgAccessAvailError :
          isString(e) ? new Error(e) : new Error('You do not have access to this tenant')
        if (error !== e && !error.cause) {
          error.cause = e
        }
        const res = { isAvail: false, error }
        setCachedValue(SPARKY_TSG_ACCESS_AVAIL_EXTPOINT, res)
        return res
      }
    }
  }
  const res = { isAvail: true }
  setCachedValue(SPARKY_TSG_ACCESS_AVAIL_EXTPOINT, res)
  return res
}
export const tsgAccessExtAvail = () => Boolean(tsgAccessExtAvailResult()?.isAvail)

export const isNavItemAvail = (navItem: NavItem | undefined): boolean => {
  const cachedGetIsNavItemAvail = getCachedValue<SparkyNavItemAvailFn>(SPARKY_NAV_ITEM_AVAIL_EXTPOINT)
  if (cachedGetIsNavItemAvail) {
    return navItem ? cachedGetIsNavItemAvail(navItem) : false
  }
  const fns = getExtensions(SPARKY_NAV_ITEM_AVAIL_EXTPOINT).reduce((fns, ext) => {
    const { isAllowed, isNavItemAvail, ignoreError } = ext
    if (runIsAllowed(isAllowed)) {
      const errorResult = Boolean(ignoreError)
      fns.push((navItem) => {
        try {
          return isNavItemAvail(navItem)
        }
        catch (e) {
          const err = isString(e) ? new Error(e) : e as Error
          err.message = `error in executing nav item avail check from ext "${(ext as InitedSparkyExtMeta).extensionId}": ${err.message || 'unknown error'}`
          console.error(err.message, err.stack)
          return errorResult
        }
      })
    }
    return fns
  }, [] as SparkyNavItemAvailFn[])
  const getIsNavItemAvail = fns.length ? (navItem: NavItem) => {
    return fns.every(fn => fn(navItem))
  } : () => true
  setCachedValue(SPARKY_NAV_ITEM_AVAIL_EXTPOINT, getIsNavItemAvail)
  return navItem ? getIsNavItemAvail(navItem) : false
}

type FeatureFlagProviderConfigWithMatcher = SparkyFeatureFlagProviderConfig & {
  matcher: ProviderMatcher
}
type GetFeatureFlagFn = <T = any>(name: string, options?: FeatureFlagOptions<T>) => T | undefined
const initFeatureFlagProviders = (): GetFeatureFlagFn | undefined => {
  const providers = getCachedValue<GetFeatureFlagFn>(SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT)
  if (providers) {
    return providers
  }
  const extensions = getExtensions(SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT)
  if (!extensions.length) {
    return
  }
  const convertRegExpToFn = (match: SparkyFeatureFlagProviderConfig['match']) => {
    return !isRegExp(match) ? match : (name: string) => match.test(name)
  }
  const flattened = extensions.reduce((flattened, ext) => {
    if (runIsAllowed(ext.isAllowed)) {
      if (Array.isArray(ext.match)) {
        for (const match of ext.match) {
          flattened.push({
            ...ext,
            match: convertRegExpToFn(match),
          })
        }
      }
      else {
        flattened.push(ext.match ? {
          ...ext,
          match: convertRegExpToFn(ext.match)
        } : ext)
      }
    }
    return flattened
  }, [] as SparkyFeatureFlagProviderConfig[])

  const matchAllExts = [] as SparkyFeatureFlagProviderConfig[]
  const nameMapping = {} as Record<string, SparkyFeatureFlagProviderConfig>
  const fromMapping = {} as Record<string, FeatureFlagProviderConfigWithMatcher>
  const fnMatches = [] as FeatureFlagProviderConfigWithMatcher[]
  for (const ext of flattened) {
    const { match } = ext
    if (ext.fromService) {
      for (const from of ensureArray(ext.fromService)) {
        if (fromMapping[from]) {
          console.error(`ignore duplicated fromService found for "${from}" in SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT extensions.`, ext, fromMapping[from])
          continue
        }
        fromMapping[from] = ext as FeatureFlagProviderConfigWithMatcher
      }
    }
    if (!match) {
      matchAllExts.push(ext)
    }
    else if (isString(match)) {
      if (nameMapping[match]) {
        console.error(`ignore duplicated match found for "${match}" in SPARKY_SERVICE_URL_PROVIDER_EXTPOINT extensions.`)
        continue
      }
      else {
        if (match.endsWith('*')) {
          const pattern = match.slice(0, -1)
          const matcher = (name: string) => name.startsWith(pattern)
          fnMatches.push(Object.assign(ext, { matcher }))
        }
        nameMapping[match] = ext
      }
    }
    else if (isFunction(match)) {
      fnMatches.push(Object.assign(ext, { matcher: match }))
    }
    else {
      const error = new Error(`ignore invalid service URL provider extpoint match type: ${typeof match}`)
      console.error(error.message, ext)
      continue
    }
  }
  const byPriorityFn = (a: SparkyFeatureFlagProviderConfig, b: SparkyFeatureFlagProviderConfig) => (b.priority || 0) - (a.priority || 0)
  matchAllExts.sort(byPriorityFn)
  fnMatches.sort(byPriorityFn)
  const getFlag: GetFeatureFlagFn = (name: string, options) => {
    const exec = (fn: SparkyFeatureFlagProviderConfig['getFeatureFlag']) => {
      try {
        return fn(name, options || {}, stateRef.current)
      }
      catch (err) {
        const error = new Error(`error in executing feature flag provider for "${name}": ${err}`)
        error.cause = err
        console.error(error.message, err)
        // TODO: datadog
      }
    }
    const isMatch = (fn: ProviderMatcher): boolean => {
      try {
        return Boolean(fn(name))
      }
      catch (err) {
        const error = new Error(`error in executing feature flag provider for "${name}": ${err}`)
        error.cause = err
        console.error(error.message, err)
        // TODO: datadog
        return false
      }
    }
    const from = options?.from
    const fromExt = from ? fromMapping[from] : undefined
    const exactMatched = nameMapping[name]
    if (fromExt && (!fromExt.match || // no match needed
      (fromExt.matcher && isMatch(fromExt.matcher)) || // fn or regex or prefix
      (fromExt.match && fromExt.match === name)) // string match
    ) {
      const result = exec(fromExt.getFeatureFlag)
      return result ?? options?.defaultValue // no fallback in using from
    }

    if (exactMatched) {
      const result = exec(exactMatched.getFeatureFlag)
      if (result !== undefined || !exactMatched.allowFallback) {
        return result
      }
    }
    for (const ext of fnMatches) {
      if (isMatch(ext.matcher)) {
        const result = exec(ext.getFeatureFlag)
        if (result !== undefined || !ext.allowFallback) {
          return result
        }
      }
    }
    for (const ext of matchAllExts) {
      const result = exec(ext.getFeatureFlag)
      if (result !== undefined || !ext.allowFallback) {
        return result
      }
    }
  }
  setCachedValue(SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT, getFlag)
  return getFlag
}
export const getFeatureFlag = <T = any>(name: string, options?: FeatureFlagOptions<T>): T | undefined => {
  const defaultValue = options?.defaultValue
  if (!name) {
    return defaultValue
  }
  if (!options?.skipOverride) {
    const overrides = stateRef.current.featureFlagValueOverrides
    if (overrides && hasOwn(overrides, name)) {
      return overrides[name]
    }
  }
  const fromKey = options?.from ? `${options.from}:` : ''
  const noCache = options && (Object.keys(options).length > 1 || !options.from)
  const cacheKey = noCache ? undefined : // only use cache if no options or only from is provided
    `${SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT.id}:${fromKey}${name}`
  const cached = cacheKey ? getCachedValue<T>(cacheKey) : undefined
  if (cached) {
    return cached
  }
  const getFromProviders = initFeatureFlagProviders()
  const getFlagsFromSources = () => {
    const value = getFromProviders?.(name, options)
    if (value !== undefined) {
      return value
    }
    const from = options?.from
    if (!from || from === 'fx' || from === SPARKY_FRAMEWORK) {
      const { fxVars } = stateRef.current
      if (fxVars && hasOwn(fxVars, name)) {
        const value = fxVars[name] as T | undefined
        if (value !== undefined) {
          return value
        }
      }
    }
    else if (from) {
      const mfeVars = getMicroAppVars(from)
      if (mfeVars && hasOwn(mfeVars, name)) {
        const value = mfeVars[name] as T | undefined
        if (value !== undefined) {
          return value
        }
      }
    }
  }
  const flagValue = getFlagsFromSources()
  if (cacheKey && flagValue !== undefined && !stateRef.current.auth?.initializing) {
    setCachedValue(cacheKey, flagValue)
  }
  return flagValue ?? defaultValue
}
export const getPropertyValue = <T = any>(name: string, options?: FeatureFlagOptions) => {
  const props = stateRef.current.auth?.properties
  if (options?.from === TSG_ID_NONE) {
    return props?.none?.get<T>(name)
  }
  return props?.get<T>(name)
}
export const getPropertyValueAsync = async <T = any>(name: string, options?: FeatureFlagOptions) => {
  return await getPropertyValue<T>(name, options)
}
